import { useCallback } from 'react'

import { useEditor } from '@craftjs/core'
import { getRandomId } from '@craftjs/utils'
import { mapValues } from 'lodash'

export function useSerialize(nodeId) {
  const { query: { node: getNode } } = useEditor()

  return useCallback(() => {
    if (!nodeId) {
      return null
    }

    try {
      const nodesReducer = (newNodes, node) => {
        const serialized = getNode(node.id).toSerializedNode()
        const { nodes: childrenNodes, linkedNodes, parent, custom, ...data } = serialized

        const newId = mapping.get(node.id)

        let { options } = custom

        if (Array.isArray(options) && !options.length) {
          options = []
        }

        if (typeof options === 'object' && !Array.isArray(options)) {
          options = mapValues(custom.options, option => ({
            ...option,
            id: mapping.get(option.id),
          }))
        }

        newNodes[newId] = {
          ...data,
          id: newId,
          parent: node.id === rootNodeId ? null : mapping.get(parent),
          nodes: childrenNodes && childrenNodes.map(id => mapping.get(id)),
          linkedNodes: linkedNodes && mapValues(linkedNodes, id => mapping.get(id)),
          custom: {
            ...custom,
            options,
          },
        }

        return newNodes
      }

      const mapping = {
        get(id) {
          if (!this[id]) {
            this[id] = getRandomId()
          }
          return this[id]
        },
      }

      const { nodes, rootNodeId } = getNode(nodeId).toNodeTree()
      const data = Object.values(nodes).reduce(nodesReducer, {})

      return data
    } catch (error) {
      console.error('[serialize-block] Failed to serialize block', {
        nodeId,
        error,
        message: error?.message,
        stack: error?.stack,
      })

      throw error
    }
  }, [nodeId, getNode])
}

export function useSerializeWithoutRemapping(nodeId) {
  const { query: { node: getNode } } = useEditor()

  return useCallback(() => {
    if (!nodeId) {
      return null
    }

    const { nodes, rootNodeId } = getNode(nodeId).toNodeTree()

    const nodesReducer = (newNodes, node) => {
      const serialized = getNode(node.id).toSerializedNode()

      newNodes[node.id] = {
        ...serialized,
        id: node.id,
        parent: node.id === rootNodeId ? null : serialized.parent,
      }

      return newNodes
    }

    return Object.values(nodes).reduce(nodesReducer, {})
  }, [nodeId, getNode])
}

export function useParseSerialized() {
  const { query: { parseSerializedNode } } = useEditor()

  return useCallback(data =>{
    let rootNodeId = null

    const mapping = {
      get(id) {
        if (!this[id]) {
          this[id] = getRandomId()
        }
        return this[id]
      },
    }

    const nodes = Object.values(data).reduce((newNodes, node) => {
      const { nodes: childrenNodes, linkedNodes, parent, custom, ...rest } = node

      const newId = mapping.get(node.id)

      if (parent === null) {
        rootNodeId = newId
      }

      let { options } = custom

      if (Array.isArray(options) && !options.length) {
        options = []
      }

      if (typeof options === 'object' && !Array.isArray(options)) {
        options = mapValues(custom.options, option => ({
          ...option,
          id: mapping.get(option.id),
        }))
      }

      const newSerialized = {
        ...rest,
        parent: parent ? mapping.get(parent) : null,
        nodes: childrenNodes && childrenNodes.map(id => mapping.get(id)),
        linkedNodes: linkedNodes && mapValues(linkedNodes, id => mapping.get(id)),
        custom: {
          ...custom,
          options,
        },
      }

      newNodes[newId] = parseSerializedNode(newSerialized).toNode(newNode => {
        newNode.id = newId
      })

      return newNodes
    }, {})

    return { nodes, rootNodeId }
  }, [parseSerializedNode])
}
